<template>
  <b-container
    fluid
    class="w-100"
    @mouseover="onHover(`ReportViewer/Studies/${sectionconfig}/CircuitConfiguration`)"
    @mouseleave="onLeave"
  >
    <b-row>
      <TextInputConfiguration
        :controller="InputCircuitCtrller"
        :sizeinput="'sm'"
        class="mb-2"
        :eventonchange="true"
        @onChange="circuitChange(true)"
      />
    </b-row>
    <b-row>
      <div v-if="isLoadingSVG">
        <b-spinner
          variant="success"
          style="width: 2rem; height: 2rem;"
          label="Large Spinner Text Centered"
        />
      </div>
      <img
        v-if="SVGDrawHiddenCtrller.value !== ''"
        :src="SVGDrawHiddenCtrller.value"
        class="w-100"
      >
      <b-button
        v-else
        v-b-tooltip.hover.top="'Draw equivalent circuit'"
        size="sm"
        variant="primary"
        @click="getSVG"
      >
        <b-icon
          icon="brush"
          aria-hidden="true"
        /> Draw
      </b-button>
      <b-button
        v-b-tooltip.hover.top="'Search literature for circuit'"
        size="sm"
        variant="primary"
        class="ml-2"
        @click="getRecommendation"
      >
        <b-icon
          icon="signpost-split"
          aria-hidden="true"
        /> Recommend
      </b-button>
    </b-row>

    <b-row class="mt-3 mb-1">
      <p class="subtitle m-0 p-0">
        Initial guess
      </p>
    </b-row>
    <b-row
      v-for="(element, index) in elementsCircuit"
      :key="index"
      class="w-100"
      align-v="center"
    >
      <b-col
        cols="3"
        align-self="center"
      >
        <p class="name-element-label">
          {{ element }}:
        </p>
      </b-col>
      <b-col
        align-self="center"
        cols="3"
      >
        <input
          v-model="InitialGuessHiddenCtrller.value[index].value"
          type="text"
          class="value-element-input"
          @change="validateText(element, index)"
        >
      </b-col>
      <b-col
        cols="4"
        align-self="center"
      >
        <b-dropdown
          split
          split-variant="outline-primary"
          variant="primary"
          :text="inititalGuessAux[index].textSelection"
          class="combobox-config shadow-sm"
          size="sm"
        >
          <b-dropdown-item
            v-for="option in inititalGuessAux[index].options"
            :key="option.value"
            :value="option.value"
            @click="changeSelection(option.text, option.value, element, index)"
          >
            {{ option.text }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
      <b-col
        cols="1"
        align-self="center"
      >
        <b-form-checkbox
          v-model="InitialGuessHiddenCtrller.value[index].constant"
          v-b-tooltip.hover.left="InitialGuessHiddenCtrller.value[index].constant ? 'Constant' : 'Variable'"
          size="sm"
          @change="updateDict(element, index)"
        />
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { ConfigName } from '@/common/sharedDefinitions/commonPlotDefinitions'
import {  inputTextConfCtrller, hiddenConfCtrller } from '@/assets/classes/configuration.js'
import TextInputConfiguration from '@/components/properties/elements/TextInputConfiguration'
import store from '@/store'
import { getDataFiles } from '@/functions/extractData.js'

const fitName = 'equivalentCircuit'

const RE_EXTRACT_COMPONENTS = /([RWCLP])[0-9]+/gm
const RE_VALID_COMPONENTS = /^[RWCLP0-9\-[\], ]+$/
const notifyZone = 'CircuitConfig'

const optionsPerElement = {
    'R': [
        { text: 'mΩ', value: 1E-3 },
        { text: 'Ω', value: 1 },
        { text: 'kΩ', value: 1E3 },
        { text: 'MΩ', value: 1E6 },
        { text: 'GΩ', value: 1E9 }
    ],
    'C': [
        { text: 'F', value: 1 },
        { text: 'mF', value: 1E-3 },
        { text: 'μF', value: 1E-6 },
        { text: 'nF', value: 1E-9 },
        { text: 'pF', value: 1E-12 },
        { text: 'fF', value: 1E-15 }
    ],
    'L': [
        { text: 'H', value: 1 },
        { text: 'mH', value: 1E-3 },
        { text: 'μH', value: 1e-6 },
        { text: 'nH', value: 1E-9 },
        { text: 'pH', value: 1E-12 },
        { text: 'fH', value: 1E-15 }
    ],
    'W': [
        { text: 'mσ', value: 1E-3 },
        { text: 'σ', value: 1 },
        { text: 'kσ', value: 1E3 },
        { text: 'Mσ', value: 1E6 },
        { text: 'Gσ', value: 1E9 }
    ],
    'P': [
        { text: 'T', value: 1 },
        { text: 'mT', value: 1E-3 },
        { text: 'μT', value: 1E-6 },
        { text: 'nT', value: 1E-9 },
        { text: 'pT', value: 1E-12 },
        { text: 'fT', value: 1E-15 }
    ],
    'P-α': [
        { text: 'Φ', value: 1 },
    ],
    'G-R': [
        { text: 'mΩ', value: 1E-3 },
        { text: 'Ω', value: 1 },
        { text: 'kΩ', value: 1E3 },
        { text: 'MΩ', value: 1E6 },
        { text: 'GΩ', value: 1E9 }
    ],
    'G-t': [
        { text: 'ms', value: 1E-3 },
        { text: 's', value: 1 },
        { text: 'ks', value: 1E3 },
    ],
    'O': [
        { text: 'mΩ', value: 1E-3 },
        { text: 'Ω', value: 1 },
        { text: 'kΩ', value: 1E3 },
        { text: 'MΩ', value: 1E6 },
        { text: 'GΩ', value: 1E9 }
    ],
    'O-τ': [
        { text: 'ms', value: 1E-3 },
        { text: 's', value: 1 },
        { text: 'ks', value: 1E3 },
    ],
    'T': [
        { text: 'mΩ', value: 1E-3 },
        { text: 'Ω', value: 1 },
        { text: 'kΩ', value: 1E3 },
        { text: 'MΩ', value: 1E6 },
        { text: 'GΩ', value: 1E9 }
    ],
    'T-τ': [
        { text: 'ms', value: 1E-3 },
        { text: 's', value: 1 },
        { text: 'ks', value: 1E3 },
    ]
}


export default {
    name: 'CircuitConfiguration',
    components: {
        TextInputConfiguration
    },
    props: {
        sectionconfig: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            InputCircuitCtrller: new inputTextConfCtrller(`${ConfigName.BaseCurveFit}.${fitName}.Circuit`, 'Circuit', 'Circuit not valid. Please only use the components: R,W,C,L,P. For parallel elements use something like: [R1-W1,P1]', 'R0-[R1-W1,P1]', (value) => { return RE_VALID_COMPONENTS.test(value) }),
            InitialGuessHiddenCtrller: new hiddenConfCtrller(`${ConfigName.BaseCurveFit}.${fitName}.InitialGuess`, []),
            SVGDrawHiddenCtrller: new hiddenConfCtrller(`${ConfigName.BasePropStudy}.SVGDrawCircuit`, ''),
            elementsConfig: [],
            elementsCircuit: [],
            objtValuesCircuit: {},
            inititalGuessAux: [],
            isLoadingSVG: false
        }
    },
    computed: mapGetters(['getDrawCircuitMiddleware', 'getRecommendCircuitMiddleware']),
    mounted() {
        this.elementsConfig = [
            this.InputCircuitCtrller,
            this.InitialGuessHiddenCtrller,
            this.SVGDrawHiddenCtrller
        ]
        this.objtValuesCircuit = {}
        try {
            this.SVGDrawHiddenCtrller.value = ''
            this.elementsConfig.forEach(conf => conf.configCategory = this.sectionconfig)
            this.elementsConfig.forEach(conf => conf.setParent(this))
            //Fill the local dictionary
            this.updateElementsCircuit()
            for (let j = 0; j < this.elementsCircuit.length; j++) {
                if (j === this.InitialGuessHiddenCtrller.value.length || typeof (this.InitialGuessHiddenCtrller.value[j]) !== 'object') {
                    break
                }
                //Fill the dictionary with the elements name and the initial value
                this.objtValuesCircuit[this.elementsCircuit[j]] = this.InitialGuessHiddenCtrller.value[j]
                if (typeof (this.InitialGuessHiddenCtrller.value[j].unitFactor) === 'undefined') {
                    // If it is not defined it is set to 1, so it is the base
                    this.InitialGuessHiddenCtrller.value[j].unitFactor = this.getDefaultValue(this.elementsCircuit[j])
                }
            }
            //this.circuitChange() //Then update the values in the initial guess fields
        } catch (error) {
            const warnText = 'Failed to load initial guess. Please contact your administrator.'
            const warnTitle = 'Error loading initial guess'

            this.$notifTerm.addNotification(this.$notifTerm.typesNotif.WARNING, warnTitle, warnText, notifyZone)
        }
    },
    methods: {
        ...mapActions([
          'setContextOnHover', 
          'drawCircuitMiddleware',
          'apiRecommendCircuitMiddleware',
          'setConfigReportModifiedReportViewer'
          ]),
        onHover(route) {
            this.setContextOnHover(route)
        },
        onLeave() {
            this.setContextOnHover(null)
        },
        getDefaultValue(type) {
            if (type.includes('R')) { return 1E3 }
            else if (type.includes('C')) { return 1E-9 }
            else if (type.includes('L')) { return 1E-3 }
            else if (type.includes('W')) { return 1E3 }
            else if (/(^P$)/.test(type)) { return 1E-9 }
            return 1
        },
        getAuxObject(type) {
            const value = this.getDefaultValue(type)
            const initialText = optionsPerElement[type].find(opt => opt.value === value).text
            return { options: optionsPerElement[type], textSelection: initialText }
        },
        updateElementsCircuit() {
            const textValue = '' + this.InputCircuitCtrller.value
            this.elementsCircuit = []
            this.inititalGuessAux = []
            let match = RE_EXTRACT_COMPONENTS.exec(textValue)
            while (match != null) {
                const newElement = match[0]
                if (match[1] === 'P') {
                    this.elementsCircuit.push(newElement)
                    this.inititalGuessAux.push(this.getAuxObject('P'))
                    this.elementsCircuit.push(newElement + '-α')
                    this.inititalGuessAux.push(this.getAuxObject('P-α'))
                } else if (match[1] === 'G') {
                    this.elementsCircuit.push(newElement + '-R')
                    this.inititalGuessAux.push(this.getAuxObject('G-R'))
                    this.elementsCircuit.push(newElement + '-t')
                    this.inititalGuessAux.push(this.getAuxObject('G-t'))
                } else if (match[1] === 'O') {
                    this.elementsCircuit.push(newElement)
                    this.inititalGuessAux.push(this.getAuxObject('O'))
                    this.elementsCircuit.push(newElement + '-τ')
                    this.inititalGuessAux.push(this.getAuxObject('O-τ'))
                } else if (match[1] === 'T') {
                    this.elementsCircuit.push(newElement)
                    this.inititalGuessAux.push(this.getAuxObject('T'))
                    this.elementsCircuit.push(newElement + '-τ')
                    this.inititalGuessAux.push(this.getAuxObject('T-τ'))
                } else {
                    this.elementsCircuit.push(newElement)
                    this.inititalGuessAux.push(this.getAuxObject(match[1]))
                }
                match = RE_EXTRACT_COMPONENTS.exec(textValue)
            }
        },
        circuitChange(clearSVG = true) {
            this.updateElementsCircuit()
            if (clearSVG) {
                this.SVGDrawHiddenCtrller.value = ''
            }
            this.InitialGuessHiddenCtrller.setValue([])
            for (let j = 0; j < this.elementsCircuit.length; j++) {
                const newElement = this.elementsCircuit[j]  // Retrieve the new element
                if (typeof (this.objtValuesCircuit[newElement]) !== 'undefined') {
                    this.InitialGuessHiddenCtrller.value.push(this.objtValuesCircuit[newElement])
                } else {
                    this.InitialGuessHiddenCtrller.value.push({
                        value: '1',
                        constant: false,
                        unitFactor: this.getDefaultValue(this.elementsCircuit[j])
                    })
                }
                // This is required so we ensure consistency on the display elements and the hidden
                this.InitialGuessHiddenCtrller.value[j].textSelection = this.inititalGuessAux[j].options.find(op => op.value === this.InitialGuessHiddenCtrller.value[j].unitFactor).text
                this.inititalGuessAux[j].textSelection = this.InitialGuessHiddenCtrller.value[j].textSelection  // Element required so the vue watcher detects it
                // We update our latest value
                this.objtValuesCircuit[newElement] = this.InitialGuessHiddenCtrller.value[j]
            }
        },
        validateText(key, indexArray) {
            const numVal = this.InitialGuessHiddenCtrller.value[indexArray].value
            if (numVal == '' || isNaN(numVal) || isNaN(parseFloat(numVal))) {
                const warnText = 'The value must be a number'
                const warnTitle = 'No valid input'
                this.$notifTerm.addNotification(this.$notifTerm.typesNotif.WARNING, warnTitle, warnText, notifyZone)
                this.InitialGuessHiddenCtrller.value[indexArray].value = 1
                return
            }
            const floatVal = parseFloat(numVal)
            if (floatVal < 0) {
                this.InitialGuessHiddenCtrller.value[indexArray].value = (-floatVal).toString()
                const infoText = 'The value must be a positive value'
                const infoTitle = 'Automatic modification'
                this.$notifTerm.addNotification(this.$notifTerm.typesNotif.INFO, infoTitle, infoText, notifyZone)
            }
            this.updateDict(key, indexArray)
        },
        updateDict(key, indexArray) {
            this.objtValuesCircuit[key] = this.InitialGuessHiddenCtrller.value[indexArray]
            this.setConfigReportModifiedReportViewer()
        },
        changeSelection(newText, newValue, element, index) {
            this.InitialGuessHiddenCtrller.value[index].textSelection = newText
            this.inititalGuessAux[index].textSelection = newText // Required because otherwise the watch in vue does not detect it
            this.InitialGuessHiddenCtrller.value[index].unitFactor = newValue
            this.updateDict(element, index)
        },
        async getSVG() {
            this.setConfigReportModifiedReportViewer()
            try {
                this.isLoadingSVG = true
                const circ = this.InputCircuitCtrller.value;
                const success = await this.drawCircuitMiddleware({ config: { circ } });
                let drawCircuit = null
                if (success) {
                    drawCircuit = this.getDrawCircuitMiddleware //Read the report obtained
                }
                if (typeof (drawCircuit) !== 'undefined' && drawCircuit !== null) {
                    this.SVGDrawHiddenCtrller.setValue(drawCircuit)
                }
            } catch (error) {

                const errText = 'Error retrieving the SVG. Message: ' + error
                const errTitle = 'Conection failed'
                this.$notifTerm.addNotification(this.$notifTerm.typesNotif.ERROR, errTitle, errText, notifyZone)
            } finally {
                this.isLoadingSVG = false
            }
        },
        async getRecommendation() {

            const dataFiles = await getDataFiles(store.getters.getCurrentReportViewer)
            if (dataFiles.length === 0) {
                const infoText = 'No files found on the report. Please add at least one file'
                const infoTitle = 'Empty report'
                this.$notifTerm.addNotification(this.$notifTerm.typesNotif.INFO, infoTitle, infoText, notifyZone)
                return
            }

            this.setConfigReportModifiedReportViewer()
            this.isLoadingSVG = true
            let recCircuit = null


            const dictDatalines = dataFiles[0].extractedData
            const success = await this.apiRecommendCircuitMiddleware(dictDatalines[0])

            if (success) {
                recCircuit = this.getRecommendCircuitMiddleware;

                if (typeof (recCircuit) !== 'undefined' && recCircuit !== null) {
                    this.InputCircuitCtrller.value = this.getRecommendCircuitMiddleware.circuit
                    const tooltip = this.getRecommendCircuitMiddleware.tooltip
                    this.$notifTerm.addNotification(this.$notifTerm.typesNotif.SUCCESS, 'Match found', tooltip, notifyZone)
                    this.circuitChange()
                }
                else {
                    this.InputCircuitCtrller.value = 'Empty'
                    this.$notifTerm.addNotification(this.$notifTerm.typesNotif.INFO, 'No match found', 'We could not find a match for the data in the literature', notifyZone)
                }

            } else {
                const warnText = 'No match found for the data provided'
                const warnTitle = 'No match'
                this.$notifTerm.addNotification(this.$notifTerm.typesNotif.WARNING, warnTitle, warnText, notifyZone)
            }
            this.isLoadingSVG = false

        }

    }

}
</script>

<style scoped>
.range-display {
    font-weight: bold;
    text-align: center;
    font-size: 0.8;
}

.subtitle {
    font-weight: bold;
}

.value-element-input {
    font-size: 0.85rem;
    width: 3.5rem;
}

.name-element-label {
    font-size: 0.85rem;
}

.combobox-config {
    font-size: 0.85rem;
}
</style>
